import React, { useState, useEffect } from "react";
import { X, Calendar, MapPin, ArrowUpRight } from "lucide-react";
import { MEDIA } from "../../lib/constants";

// Soft-opening event date. Banner auto-hides at 11:59 PM ET on this day.
// After July 8, 2026 (11:59 PM Eastern), this component renders null.
const EVENT_HIDE_AT = new Date("2026-07-09T04:00:00Z"); // midnight ET → 04:00 UTC next day

const DISMISS_KEY = "dwc:soft-opening-dismissed";

export default function SoftOpeningBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Do not render past the event
    if (Date.now() >= EVENT_HIDE_AT.getTime()) return;
    // Respect user's dismiss preference
    if (typeof window !== "undefined" && window.localStorage.getItem(DISMISS_KEY) === "1") return;
    setVisible(true);
  }, []);

  const dismiss = () => {
    window.localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <section
      id="soft-opening"
      className="relative bg-gradient-to-br from-[#F6F1E4] via-[#EFE8D2] to-[#F6F1E4] border-b border-black/10 overflow-hidden"
      data-testid="soft-opening-banner"
    >
      {/* Decorative leaf tint */}
      <div className="absolute inset-0 pointer-events-none opacity-40"
           style={{ backgroundImage: "radial-gradient(circle at 90% 30%, rgba(252,121,51,0.15), transparent 50%), radial-gradient(circle at 5% 80%, rgba(10,80,20,0.12), transparent 55%)" }} />

      <div className="relative max-w-7xl mx-auto px-6 py-10 sm:py-14 grid lg:grid-cols-12 gap-8 items-center">
        {/* Flyer image */}
        <div className="lg:col-span-4 flex justify-center">
          <a
            href={MEDIA.softOpeningFlyer}
            target="_blank"
            rel="noreferrer"
            className="block max-w-[280px] sm:max-w-xs relative group"
            data-testid="soft-opening-flyer-link"
          >
            <img
              src={MEDIA.softOpeningFlyer}
              alt="District Wellness Center Soft Opening — Wednesday July 8th"
              loading="lazy"
              className="w-full h-auto shadow-2xl group-hover:scale-[1.02] transition-transform duration-500"
            />
          </a>
        </div>

        {/* Copy */}
        <div className="lg:col-span-8">
          <div className="text-[10px] uppercase tracking-[0.4em] text-brand mb-3">
            You&rsquo;re Invited · Friends & Family
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-[#0B3B1F]">
            <span className="italic text-brand">Soft Opening</span> — July 8<sup className="text-lg">th</sup>.
          </h2>
          <p className="mt-5 text-ink/70 text-base sm:text-lg leading-relaxed max-w-2xl">
            Come celebrate with us! Wednesday, July 8th, 11 AM – 6 PM at our Uptown
            location. Welcoming Washington DC & out-of-state patients. We appreciate
            your support and can&rsquo;t wait to welcome you.
          </p>

          <div className="mt-6 grid sm:grid-cols-3 gap-4 max-w-2xl">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-brand/15 flex items-center justify-center shrink-0">
                <Calendar size={16} className="text-brand" />
              </div>
              <div>
                <div className="text-sm font-medium text-ink">Wednesday, July 8</div>
                <div className="text-xs text-ink/50">11 AM – 6 PM</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-brand/15 flex items-center justify-center shrink-0">
                <MapPin size={16} className="text-brand" />
              </div>
              <div>
                <div className="text-sm font-medium text-ink">Uptown Location</div>
                <div className="text-xs text-ink/50">4915 Georgia Ave NW</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-brand/15 flex items-center justify-center shrink-0 font-serif text-brand text-sm font-bold">
                21+
              </div>
              <div>
                <div className="text-sm font-medium text-ink">Medical patients</div>
                <div className="text-xs text-ink/50">Must self-certify prior</div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#medical-access"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-6 py-3.5 text-[11px] uppercase tracking-[0.25em] font-bold transition-colors"
              data-testid="soft-opening-certify-cta"
            >
              Self-certify now
              <ArrowUpRight size={14} />
            </a>
            <a
              href="https://www.instagram.com/districtwellnesscenter"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 border border-ink/20 text-ink hover:border-brand hover:text-brand px-6 py-3.5 text-[11px] uppercase tracking-[0.25em] transition-colors"
              data-testid="soft-opening-instagram-cta"
            >
              Follow @districtwellnesscenter
              <ArrowUpRight size={12} />
            </a>
          </div>
        </div>

        {/* Dismiss */}
        <button
          onClick={dismiss}
          aria-label="Dismiss announcement"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-ink/40 hover:text-ink hover:bg-black/5 transition-colors"
          data-testid="soft-opening-dismiss"
        >
          <X size={18} />
        </button>
      </div>
    </section>
  );
}
